// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { SeoRepoService, SeoRepoType } from '../../services/seo-repo.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SeoHelpMessageService } from '@client/app/blocks/service/seo/seo-help-message.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import {
    SeoDialogPagesWithDuplicateTitleComponent,
} from '@client/app/pages/seo/dialogs/pages-with-duplicate-title/seo-dialog-pages-with-duplicate-title.component';

import { PagesFromSeoIssuesComponent } from '@client/app/pages/seo/dialogs/pages-from-seo-issues/pages-from-seo-issues.component';
import {
    PagesWithDuplicateTitlePagesComponent,
} from '@client/app/pages/seo/dialogs/pages-with-duplicate-title/blocks/pages/pages-with-duplicate-title-pages.component';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';

@Component({
    selector: 'pages-seo-issues',
    templateUrl: './pages-seo-issues.component.html',
    standalone: false,
})
export class PagesSeoIssuesComponent implements OnInit {
    @Input() header: string = '';
    @Input() classification: string = '';
    @Input() pageCount: number = 0;
    @Input() subHeader?: any;
    @Input() faIcon?: any;
    @Input() theme?: any;
    @Input() category?: any;

    target = 'seo';
    pages: SeoRepoType[] = [];
    promise: Promise<any> | null = null;
    progress: Promise<any> | null = null;

    constructor (
        private seoRepoService: SeoRepoService,
        private monEventsService: MonEventService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
        private seoHelpMessageService: SeoHelpMessageService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.theme = this.theme || 'link';
        this.pageCount = this.pageCount || 0;
        this.getPage();
    }

    getPage (): void {
        const params: any = {
            classification: this.classification,
        };

        this.promise = this.seoRepoService.getAll(params).then((data) => {
            this.pages = data.sort((x: SeoRepoType, y: SeoRepoType) => {
                if (x.ignored || y.ignored) {
                    if (x.ignored && !y.ignored) {
                        return 1;
                    } else if (!x.ignored && y.ignored) {
                        return -1;
                    }
                }
                return x.pages_count > y.pages_count ? -1 : x.pages_count < y.pages_count ? 1 : 0;
            });
            this.updateDropdowns();
            return this.pages;
        });
    }

    updateDropdowns (): void {
        for (const seo of this.pages) {
            const dropdown = [
                {
                    label: this.translateService.getString('Ignore'),
                    action: () => this.ignoreCheckpoint(seo),
                    eventParams: { action: 'seo-ignore' },
                    shouldShow: !seo.ignored,
                },
                {
                    label: this.translateService.getString('Unignore'),
                    action: () => this.unignoreCheckpoint(seo),
                    eventParams: { action: 'seo-unignore' },
                    shouldShow: seo.ignored,
                },
            ];

            (seo as any).dropdown = dropdown.filter((option) => option.shouldShow !== false).map(item => {
                return {
                    label: item.label,
                    action: () => {
                        this.monEventsService.run('actionTrigger', item.eventParams);
                        item.action();
                    },
                };
            });
        }
    }

    ignoreCheckpoint (checkpoint: SeoRepoType): void {
        let confirmText = '<strong>' + this.translateSeoIssue(checkpoint.name) + '</strong>';
        confirmText += '<br /><br />' + this.translateService.getString('Add reason (Optional)');

        this.monModalService.prompt(confirmText).then((result: string) => {
            const updateData = {
                ...checkpoint,
                comment: result,
                ignored: true,
            };
            this.progress = this.seoRepoService.update(checkpoint.id, updateData).then(() => {
                this.getPage();
            }).catch(() => {
                this.getPage();
            });
        }).catch(() => {
            // User cancelled
        });
    }

    unignoreCheckpoint (checkpoint: SeoRepoType): void {
        let confirmText = '<strong>' + this.translateSeoIssue(checkpoint.name) + '</strong>';
        confirmText += '<br /><br />' + this.translateService.getString('Add reason (Optional)');

        this.monModalService.prompt(confirmText).then((result: string) => {
            const updateData = {
                ...checkpoint,
                comment: result,
                ignored: false,
            };
            this.progress = this.seoRepoService
                .update(checkpoint.id, updateData)
                .then(() => {
                    if (!updateData.ignored) {
                        let alertMessage =
                            '<strong>' + this.translateService.getString('Checkpoint has been updated.') + '</strong><br/><br/>';
                        alertMessage += this.translateService.getString(
                            'The Checkpoint [[checkpoint]] will be updated with data after next crawl',
                            {
                                checkpoint: '<code>' + this.translateSeoIssue(checkpoint.name) + '</code>',
                            },
                        );
                        this.monModalService.alert(alertMessage);
                    }
                }).catch(() => {
                    // Handle error
                }).finally(() => {
                    this.getPage();
                });
        }).catch(() => {
            // User cancelled
        });
    }

    sortPage (): void {
        this.getPage();
    }

    pagesList (issue: SeoRepoType): void {
        let dialogBody: any;

        switch (issue.name) {
            case 'seo_duplicate_title':
            case 'seo_duplicate_h1':
                dialogBody = SeoDialogPagesWithDuplicateTitleComponent;
                break;
            case 'seo_pages_with_duplicate_title':
                dialogBody = PagesWithDuplicateTitlePagesComponent;
                break;
            default:
                dialogBody = PagesFromSeoIssuesComponent;
        }

        const params = {
            body: dialogBody,
            size: 'lg',
            data: { issue: issue, pageCount: this.pageCount },
        };
        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params, callback: () => this.getPage() });
    }

    canShowHelpMessage (seo: SeoRepoType): boolean {
        return this.seoHelpMessageService.canShowMessageForDomain(seo);
    }

    getHelpMessage (seo: SeoRepoType): string {
        return this.seoHelpMessageService.getHelpMessage(seo);
    }

    trackByIndex (index: number, item: SeoRepoType): number {
        return index;
    }

    private translateSeoIssue (name: string): string {
        // This should use the translateSeoIssue pipe equivalent
        // For now, return the name as-is, this should be replaced with proper translation
        return this.pipeService.get('translateSeoIssue')(name);
    }
}
